import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { AiBarChart } from "../charts-and-tables/AiBarChart";
import { AiLineChart } from "../charts-and-tables/AiLineChart";
import { AiPieChart } from "../charts-and-tables/AiPieChart";
import { AiTable } from "../charts-and-tables/AiTable";
import { AiSuggestedQuestions } from "../AiSuggestedQuestions";
import {
  extractFirstMatchingToolCallPayload,
  extractTextFromMessageContent,
  parseBarChartFromToolArgs,
  parseLineChartFromToolArgs,
  parsePieChartFromToolArgs,
  parseSuggestedQuestionsFromToolArgs,
  parseTableFromToolArgs,
} from "../ai-chat-message-utils";
import type { AgentMessage } from "../ai-chat-message-utils";
import { useMessageCopyState } from "./useMessageCopyState";
import { AssistantMessageActions } from "./AssistantMessageActions";

interface AssistantMessageProps {
  message: Extract<AgentMessage, { role: "assistant" }>;
  onQuestionSelected?: (question: string) => void;
}

export const AssistantMessage = ({
  message,
  onQuestionSelected,
}: AssistantMessageProps) => {
  const messageText = extractTextFromMessageContent(message.content);
  const { isCopied, copyResponseText } = useMessageCopyState(messageText);
  const toolCalls = message.toolCalls;

  const barChartPayload = extractFirstMatchingToolCallPayload(
    toolCalls,
    parseBarChartFromToolArgs,
  );
  const pieChartPayload = extractFirstMatchingToolCallPayload(
    toolCalls,
    parsePieChartFromToolArgs,
  );
  const lineChartPayload = extractFirstMatchingToolCallPayload(
    toolCalls,
    parseLineChartFromToolArgs,
  );
  const tablePayload = extractFirstMatchingToolCallPayload(
    toolCalls,
    parseTableFromToolArgs,
  );
  const suggestedQuestionsPayload = extractFirstMatchingToolCallPayload(
    toolCalls,
    parseSuggestedQuestionsFromToolArgs,
  );

  const hasChartPayload =
    barChartPayload || pieChartPayload || lineChartPayload || tablePayload;

  if (hasChartPayload) {
    return (
      <div className="tcw-message tcw-message--assistant">
        <div className="tcw-assistant-wrapper">
          {barChartPayload && <AiBarChart payload={barChartPayload} />}
          {pieChartPayload && <AiPieChart payload={pieChartPayload} />}
          {lineChartPayload && <AiLineChart payload={lineChartPayload} />}
          {tablePayload && <AiTable payload={tablePayload} />}
          {messageText && (
            <div className="tcw-bubble tcw-bubble--assistant">
              <div className="tcw-markdown">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {messageText}
                </ReactMarkdown>
              </div>
            </div>
          )}
          {suggestedQuestionsPayload && onQuestionSelected && (
            <AiSuggestedQuestions
              label={suggestedQuestionsPayload.label}
              questions={suggestedQuestionsPayload.questions}
              onQuestionSelected={onQuestionSelected}
            />
          )}
        </div>
      </div>
    );
  }

  if (suggestedQuestionsPayload && !messageText) {
    if (!onQuestionSelected) return null;
    return (
      <div className="tcw-message tcw-message--suggestions">
        <AiSuggestedQuestions
          label={suggestedQuestionsPayload.label}
          questions={suggestedQuestionsPayload.questions}
          onQuestionSelected={onQuestionSelected}
        />
      </div>
    );
  }

  const pendingToolCalls =
    !messageText && toolCalls?.length ? toolCalls : null;

  return (
    <div className="tcw-message tcw-message--assistant">
      <div className="tcw-assistant-wrapper">
        <div className="tcw-bubble tcw-bubble--assistant">
          {messageText && (
            <div className="tcw-markdown">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {messageText}
              </ReactMarkdown>
            </div>
          )}
          {pendingToolCalls && (
            <p className="tcw-tool-call">
              {pendingToolCalls[0].function.name
                .replace(/([A-Z])/g, " $1")
                .toLowerCase()
                .trim()}
              …
            </p>
          )}
        </div>
        {suggestedQuestionsPayload && onQuestionSelected && (
          <AiSuggestedQuestions
            label={suggestedQuestionsPayload.label}
            questions={suggestedQuestionsPayload.questions}
            onQuestionSelected={onQuestionSelected}
          />
        )}
        {messageText && (
          <AssistantMessageActions
            isCopied={isCopied}
            onCopyResponseText={copyResponseText}
          />
        )}
      </div>
    </div>
  );
};
